import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { BillType } from '../../../all-types/bill'
import { useTheme } from '@mui/material'
import { getFormattedDate, getFormattedTime } from '../../../utils'

type orderCardProps = {
  bill: BillType
  selected?: boolean
  onSelect?: () => void
}

/** Normalize take-away statuses coming from server into consistent labels */
const normalizeTakeAwayStatus = (raw?: string) => {
  if (!raw) return 'Pending'
  const v = raw.toUpperCase().replace(/[\s-]+/g, '_')
  if (['DONE', 'COMPLETED', 'DELIVERED'].includes(v)) return 'Done'
  if (v === 'READY') return 'Ready'
  if (['IN_PROGRESS', 'COOKING', 'PROCESSING'].includes(v)) return 'In-Progress'
  if (v === 'PENDING') return 'Pending'
  return v.charAt(0) + v.slice(1).toLowerCase() // fallback: Title Case-ish
}

/** Decide label and dot color */
const getStatusForCard = (bill: any, theme: any) => {
  // DINE-IN: keep your original "Done" + existing color
  if (bill?.orderType === 'DINE-IN') {
    return {
      label: 'Done',
      dotBg: theme.palette.color3?.main ?? 'success.main',
    }
  }

  // TAKEAWAY: use takeAwayStatus
  const label = normalizeTakeAwayStatus(bill?.takeAwayStatus)
  const dotBg =
    label === 'Done'
      ? 'success.main'
      : label === 'Ready'
      ? 'info.main'
      : label === 'In-Progress'
      ? 'warning.main'
      : 'text.disabled' // Pending / unknown

  return { label, dotBg }
}

function OrderCard({ bill, selected, onSelect }: orderCardProps) {
  const theme = useTheme()

  // Safe fallbacks to avoid runtime errors
  const tableNo = bill?.info?.tableNo ?? '-'
  const itemsCount = Array.isArray(bill?.items) ? bill.items.length : 0
  const billNo = bill?.billNo ?? ''
  const finalAmount =
    bill?.billingInfo?.finalBillAmount ??
    0

  const ts =
    Number(bill?.timeInfo?.timestamp) ||
    (bill?.createdAt ? Date.parse(bill.createdAt) : Date.now())

  const { label: statusLabel, dotBg } = getStatusForCard(bill, theme)

  return (
    <Paper
      elevation={4}
      onClick={onSelect}
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: '15px',
        cursor: 'pointer',
        border: selected ? `1px solid ${theme.palette.CommonBlack.main}` : '',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ fontWeight: 700 }}>
          Serial No. {bill?.sNo ?? '-'}
          <Typography
            sx={{ color: theme.palette.offline.main, fontSize: 12, fontWeight: 500, pt: '5px' }}
          >
            Table {tableNo} | {itemsCount} {itemsCount === 1 ? 'item' : 'items'} | BNo. {billNo}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: 8,
                height: 8,
                bgcolor: dotBg, // <-- status-driven color
                borderRadius: 100,
              }}
            />
            <Typography
              sx={{
                textAlign: 'center',
                textTransform: 'lowercase',
                mr: 0.5,
                fontWeight: 500,
                color: theme.palette.offline.main,
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {statusLabel}
            </Typography>
          </Box>

          <Typography
            sx={{
              textTransform: 'lowercase',
              color: theme.palette.offline.main,
              fontSize: 10,
              '&::first-letter': { textTransform: 'uppercase' },
            }}
          >
            {bill?.orderType}
            {/* keep takeAwayType hidden as in your original */}
          </Typography>
        </Box>
      </Box>

      {/* Billing Info */}
      <Box sx={{ fontWeight: 500 }}>
        ₹{finalAmount}
        <Box sx={{ color: theme.palette.offline.main, fontSize: 8, fontWeight: 500, pt: '5px' }}>
          {getFormattedTime(ts)}
          <Box>{getFormattedDate(ts)}</Box>
        </Box>
      </Box>
    </Paper>
  )
}

export { OrderCard }
